import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { useNavigate, useSearchParams } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";

import Illustration from "../../assets/images/image-2.png";
import { AuthPageWrapper } from "./auth.styled";
import FooterOne from "../../components/footer/footerOne";
import CreatePswdBlock from "./signupComponents/createPswdBlock";
import RenderIf from "../../components/renderIf";
import { ROUTE_NAMES } from "../../constants/routeNames";

interface ResetPasswordData {
  userID: number;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
}

export const ResetPasswordPage = () => {
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetData, setResetData] = useState<ResetPasswordData | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const token = searchParams.get("token");

  useEffect(() => {
    validateToken();
  }, []);

  const validateToken = async () => {
    try {
      if (!token) {
        setErrorMessage("No password reset token provided");
        setIsLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/validatePasswordResetToken?token=${token}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        setErrorMessage(errorData.message || "Invalid or expired reset link");
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      if (data.valid) {
        setResetData({
          userID: data.userID,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          companyName: data.companyName,
        });
        setTokenValid(true);
      } else {
        setErrorMessage("Reset token validation failed");
      }
    } catch (error: any) {
      console.error("Error validating token:", error);
      setErrorMessage("Error validating reset link. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = () => {
    toast.success("Password set successfully! Redirecting to login...", {
      autoClose: 2000,
    });
    setTimeout(() => {
      navigation(ROUTE_NAMES.Initial);
    }, 2000);
  };

  return (
    <>
      <AuthPageWrapper>
        <Container fluid className="h-100">
          <Row className="h-100">
            <Col md={6} className="d-none d-md-flex align-items-center">
              <img src={Illustration} alt="illustration" className="w-100" />
            </Col>
            <Col md={6} className="d-flex align-items-center justify-content-center">
              <div className="w-100 p-4">
                <RenderIf isShow={isLoading}>
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Validating reset link...</p>
                  </div>
                </RenderIf>

                <RenderIf isShow={!isLoading && errorMessage}>
                  <div className="text-center py-5">
                    <h4 className="text-danger mb-4">Invalid Reset Link</h4>
                    <p className="mb-4">{errorMessage}</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigation(ROUTE_NAMES.Initial)}
                    >
                      Back to Login
                    </button>
                  </div>
                </RenderIf>

                <RenderIf isShow={tokenValid && resetData}>
                  <div>
                    <h3 className="mb-4 text-center">Set Your Password</h3>
                    <p className="text-center text-muted mb-4">
                      Welcome, {resetData?.firstName}! Please set a strong password
                      to complete your account setup.
                    </p>
                    <CreatePswdBlock
                      emailVerified={true}
                      smsVerified={true}
                      userId={resetData?.userID}
                      onSuccess={handlePasswordReset}
                      isPasswordReset={true}
                    />
                  </div>
                </RenderIf>
              </div>
            </Col>
          </Row>
        </Container>
      </AuthPageWrapper>
      <FooterOne />
    </>
  );
};
